import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, X } from 'lucide-react';
import Contact from '../sections/Contact';

interface NewsItem {
  id: number;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  imageUrl: string;
  link: string;
  content?: React.ReactNode;
  isYoutube?: boolean;
}

const News = () => {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  // Sample news data with properly formatted content
  const newsData: NewsItem[] = [

    {
      id: 14,
      title: "UTR Therapeutics Inc. Announces U.S. FDA Orphan Drug Designation and Rare Pediatric Disease Designation for UTRxMYCN M1-14",
      excerpt: "UTRxMYCN M1-14 receives FDA ODD and RPDD for soft tissue sarcoma and rhabdomyosarcoma, highlighting a major unmet need in pediatric and adult sarcomas.",
      date: "September 29, 2026",
      readTime: "5 min",
      category: "Research",
      imageUrl: "",
      link: "#",
      content: (
        <div className="space-y-6">
          <p className="font-medium">
            NEW YORK, Sept. 29, 2026 /PRNewswire/ -- UTR Therapeutics Inc., a biotech company with clinical trial-ready assets defining a new category of RNA therapeutics to conquer the undruggable proteome, today announced that the U.S. Food and Drug Administration (FDA) has granted Orphan Drug Designation (ODD) to 3′UTRMYCN M1-14 (UTRxMYCN M1-14) for the treatment of soft tissue sarcoma, including rhabdomyosarcoma (RMS), and Rare Pediatric Disease Designation (RPDD) for the treatment of rhabdomyosarcoma.
          </p>

          <p>
            The designations underscore UTRxMYCN M1-14's therapeutic targeting of the “undruggable” MYCN (N-MYC) in rare, aggressive soft tissue sarcomas, addressing a significant unmet clinical need in pediatric and adult sarcomas.
          </p>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-bold mb-2">Why this matters</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>MYCN is a defining oncogenic driver of aggressive soft tissue sarcomas, including fusion-positive rhabdomyosarcoma.</li>
              <li>It is intrinsically disordered and lacks a conventional binding pocket, making it historically difficult to target.</li>
              <li>There are currently no clinically approved therapies that directly target MYCN.</li>
            </ul>
          </div>

          <p>
            UTR Therapeutics' core technology of ultra-targeted 3′UTR engineering targets the oncogenic mRNA of undruggable genes, overwrites their endogenous oncogenic mRNA messages, and triggers them for degradation by the nonsense-mediated decay pathway, thus eliminating the diseased mRNA while sparing the healthy mRNA.
          </p>

          <p>
            In preclinical studies, UTRxMYCN M1-14, invented by Dr. Awah at UTR Therapeutics Inc., reliably targeted and degraded the oncogenic MYCN in aggressive MYCN-driven rhabdomyosarcoma, including the deadly fusion-positive form. The drug achieved on-target specificity and therapeutic efficacy, demonstrating dose-dependent inhibition of MYCN, tumor inhibition, and inhibition of liver and lung metastasis while remaining safe.
          </p>

          <blockquote className="border-l-4 border-blue-800 pl-4 italic">
            “The ODD and RPDD designations granted to UTRxMYCN M1-14 by the U.S. FDA validate the UTR Therapeutics platform technology and the potential of UTRxMYCN M1-14 to address several aggressive sarcomas that represent serious unmet needs in the U.S. and worldwide.”
            <footer className="mt-2 font-semibold">— David T. Asuzu, M.D., Chief Medical Officer</footer>
          </blockquote>

          <div className="bg-white p-4 border rounded-lg shadow-sm">
            <h4 className="font-bold text-blue-800">About the designations</h4>
            <p className="mt-2">
              The FDA grants Orphan Drug Designation to drugs and biologics intended to treat diseases affecting fewer than 200,000 people in the United States. ODD provides seven years of U.S. market exclusivity upon approval, a tax credit for qualified clinical testing costs, and an exemption from prescription drug user fees.
            </p>
            <p className="mt-2">
              The FDA grants Rare Pediatric Disease Designation to drugs for serious or life-threatening diseases that primarily affect individuals under 18. RPDD is a prerequisite for a Rare Pediatric Disease Priority Review Voucher, which may be awarded upon FDA approval of the asset, subject to the FDA’s eligibility criteria at the time of approval.
            </p>
          </div>

          <p>
            UTR Therapeutics Inc. is a biotech company headquartered in New York City, USA, advancing a first-in-class RNA Overwriting platform delivered by a proprietary Iron Oxide Nanocage (ION) delivery system. The platform is designed to identify and kill cancer-driving transcription factors that have resisted conventional drug modalities. UTR Therapeutics has clinical-ready assets spanning c-MYC, MYCN, TEAD1, YAP1, and HER2 (ERBB2), as well as assets for neurodegenerative disorders.
          </p>
        </div>
      )
    },

{
    id: 1,
    title: "UTR Therapeutics Inc Announces IND Submission to US FDA for First-in-Human Clinical Trial of mRNA Destabilizing Drug Targeting c-MYC Driven Cancers",
    excerpt: "UTR Therapeutics submits IND application for UTRxM1-18, a novel mRNA-targeting therapeutic for c-MYC driven cancers, with a first-in-human Phase 1 trial slated for 2026.",
    date: "May 08 2025",
    readTime: "4 min",
    category: "Research",
    imageUrl: "", // You would replace this with an actual image path
    link: "#",
    content: (
      <div className="space-y-6">
        <p className="font-medium">
          NEW YORK, May 08, 2025 (GLOBE NEWSWIRE) -- UTR Therapeutics Inc (UTRx Inc.), a drug development biotech company based in New York City, USA is pleased to announce a new milestone, the submission of an Investigational New Drug (IND) application to the U.S. Food and Drug Administration (FDA) for UTRxM1-18, a novel therapeutic approach for targeting c-MYC driven cancers.
        </p>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-bold mb-2">Technology Highlights:</h3>
          <ul className="list-disc pl-5 space-y-2">
            <li>Built on UTR’s proprietary Ultra-Targeted RNA (UTR) platform</li>
            <li>Precisely degrades disease-causing mRNA transcripts with high specificity</li>
            <li>Utilizes molecular machinery only present in tumor cells, sparing healthy cells</li>
            <li>Targets c-MYC, an oncogene over-expressed in 75% of human cancers</li>
          </ul>
        </div>

        <p>
          This milestone, driven by our innovative platform 3’UTR engineering technology, precisely targets specific transcripts and degrades them. This technology supports our vision of changing how we treat and live beyond cancer.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white p-4 border rounded-lg shadow-sm">
            <h4 className="font-bold text-blue-800">Clinical Development</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>First-in-Human Phase 1 trial slated for 2026, pending FDA approval</li>
              <li>Targets c-MYC driven tumors including triple negative breast cancer, pancreatic cancer, colorectal cancer and ovarian cancer</li>
            </ul>
          </div>
          <div className="bg-white p-4 border rounded-lg shadow-sm">
            <h4 className="font-bold text-blue-800">Preclinical Data</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>Showed robust dose-dependent efficacy in human-derived tumor models</li>
              <li>Demonstrated no dose-limiting toxicities even at the highest doses tested</li>
            </ul>
          </div>
        </div>

        <h3 className="font-bold text-lg pt-4">Leadership Commentary</h3>

        <blockquote className="border-l-4 border-blue-800 pl-4 italic">
          “This IND submission is a seismic leap forward and offers patients an option where there are none. Our technology’s unique ability to therapeutically degrade mRNA transcripts redefines what’s possible in biotech.”
          <footer className="mt-2 font-semibold">— Dr. Chidiebere Awah MD, PhD, CEO and Principal Investigator</footer>
        </blockquote>

        <blockquote className="border-l-4 border-blue-800 pl-4 italic">
          “We’re not just innovating—we’re rewriting the future of cancer and disease. The validation data supports the technological abilities of engineering mRNA stability elements on the 3’UTR of oncogenes for therapeutic use.”
          <footer className="mt-2 font-semibold">— Dr David T Asuzu MD, PhD, MPH, Chief Medical Officer (Adult Cancers)</footer>
        </blockquote>

        <blockquote className="border-l-4 border-blue-800 pl-4 italic">
          “The validation data supports the technological abilities of engineering mRNA stability elements on the 3’UTR of oncogenes for therapeutic use.”
          <footer className="mt-2 font-semibold">— Dr. Kevin Struhl PhD, co-inventor of the technology and advisor to UTRx Inc.</footer>
        </blockquote>

        <p>
          UTRx Inc. is thrilled to share this milestone of IND submission with both our institutional and retail investors. We welcome connections with external partners in our quest for bringing transformative medicines through clinical development.
        </p>

        <div className="pt-2 text-sm italic">
          <p>Explore opportunities to support our mission, including unique investment vehicles for retail investors. Contact us to learn how you can contribute to our vision and share our promising future.</p>
        </div>
      </div>
    )
  },

  {
    id: 2,
    title: "UTR Therapeutics Welcomes Biotech Equity Expert Matthew Kaplan as Consultant",
    excerpt: "Seasoned biotechnology analyst and advisor Matthew Kaplan joins UTRx team, bringing 24+ years of healthcare equity research experience.",
    date: "August 1 2025",
    readTime: "3 min",
    category: "Company News",
    imageUrl: "/kaplan.jpg", // You would replace this with the actual image path
    link: "#",
    content: (
      <div className="space-y-6">
        <p className="font-medium">
          UTR Therapeutics Inc. is very pleased to announce that Matthew Kaplan, the CEO of Alander BioPharm Advisors, LLC, is joining our team as a consultant.
        </p>
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-bold mb-2">Key Qualifications:</h3>
          <ul className="list-disc pl-5 space-y-2">
            <li>24 years experience as Equity Analyst specializing in biotechnology</li>
            <li>Former Managing Director and Head of Healthcare Equity Research at Ladenburg Thalmann & Co. (2008-2025)</li>
            <li>Top ranked Biotechnology Stock Picker by Thomson Reuters, Financial Times, and Forbes</li>
            <li>Current Board Member at Nuvectis Pharma (NVCT) since 2021</li>
          </ul>
        </div>

        <p>
          Mr. Kaplan brings deep expertise in analysis and advisement of early-stage biotech companies, with a distinguished career spanning:
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white p-4 border rounded-lg shadow-sm">
            <h4 className="font-bold text-blue-800">Investment Experience</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>Partner & Director of Healthcare Research at Punk, Ziegel & Company</li>
              <li>Senior Biotechnology Analyst at Evolution Capital</li>
              <li>Director of The Life Sciences Group at The Carson Group</li>
            </ul>
          </div>
          <div className="bg-white p-4 border rounded-lg shadow-sm">
            <h4 className="font-bold text-blue-800">Scientific Background</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>6 years as Research Associate at Albert Einstein College of Medicine</li>
              <li>Co-authored numerous articles on cardiac gene regulation</li>
              <li>BS in Biology from University of Michigan</li>
            </ul>
          </div>
        </div>

        <p>
          "We are thrilled to have Matthew join our team," said UTRx leadership. "His unparalleled experience in biotech equity analysis and deep understanding of early-stage company dynamics will be invaluable as we advance our therapeutic platforms."
        </p>

        <div className="pt-2 text-sm italic">
          <p>Mr. Kaplan will provide strategic consulting on financial and corporate development matters.</p>
        </div>
      </div>
    )
  },

    {
      id: 3,
      title: "UTRx Leadership Engages Next Generation of Biotech Talent at LifeSci NYC Internship Program",
      excerpt: "Dr. Awah and investment banking veteran Matt Kaplan introduced UTRx's breakthrough technology to LifeSci NYC interns in a special fireside chat.",
      date: "May 27 2025",
      readTime: "2 min",
      category: "Company News",
      imageUrl: "/chidi.JPG",
      link: "#",
      content: (
        <div className="space-y-6">
          <p className="font-medium">
            UTR Therapeutics Inc. leadership participated in an educational fireside chat last week as part of the prestigious LifeSci NYC Internship Program.
          </p>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-bold mb-2">Event Highlights:</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Featured speakers:</strong> Dr. Awah and Matt Kaplan (seasoned healthcare investment banker/analyst)</li>
              <li><strong>Audience:</strong> Select interns from the LifeSci NYC program</li>
              <li><strong>Organizers:</strong> Sharon Kaplan and Dr. Anita Kishore</li>
            </ul>
          </div>
    
          <p>
            The discussion focused on two key areas of UTRx's innovation:
          </p>
    
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-4 border rounded-lg shadow-sm">
              <h4 className="font-bold text-blue-800">Breakthrough Technology</h4>
              <p>Detailed our novel approaches to targeting key oncogenic pathways</p>
            </div>
            <div className="bg-white p-4 border rounded-lg shadow-sm">
              <h4 className="font-bold text-blue-800">Regulatory Strategy</h4>
              <p>Insights into drug development pathways and FDA engagement</p>
            </div>
          </div>
    
          <p>
            This engagement reflects UTRx's commitment to STEM education and cultivating the next generation of biotech leaders in New York's growing life sciences ecosystem.
          </p>
    
          <div className="pt-2 text-sm italic">
            <p>Special thanks to the LifeSci NYC team for organizing this valuable knowledge-sharing opportunity.</p>
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: "UTRx is pleased to announce that we have filed 5 new patent application mid February 2025 with the USPTO and 1 PCT filed early February 2025.",
      excerpt: "Five USPTO applications and one PCT filing strengthen our position in oncology innovation, focusing on TEAD1 and YAP1 inhibition.",
      date: "February 2025",
      readTime: "3 min",
      category: "Research & Innovation",
      imageUrl: "/UTRX2025REAL.jpg",
      link: "#",
      content: (
        <div className="space-y-6">
          <p className="font-medium">
            UTR Therapeutics Inc. has significantly expanded its intellectual property portfolio with six strategic patent filings in February 2025.
          </p>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-bold mb-2">Key Filings:</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>5 USPTO applications</strong> filed mid-February 2025</li>
              <li><strong>1 PCT international application</strong> filed early February 2025</li>
            </ul>
          </div>
    
          <p>
            These filings protect our novel approaches to targeting the <strong>Hippo signaling pathway</strong>, with particular focus on:
          </p>
    
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-4 border rounded-lg shadow-sm">
              <h4 className="font-bold text-blue-800">TEAD1 Inhibitors</h4>
              <p>Novel compounds targeting this key transcriptional regulator</p>
            </div>
            <div className="bg-white p-4 border rounded-lg shadow-sm">
              <h4 className="font-bold text-blue-800">YAP1 Modulators</h4>
              <p>Innovative approaches to control this oncogenic driver</p>
            </div>
          </div>
    
          <p>
            This intellectual property expansion marks a significant milestone in our drug development pipeline, potentially enabling breakthrough therapies for cancers with Hippo pathway dysregulation.
          </p>
    
          {/* Optional contact block
          <div className="pt-2 text-sm">
            <p>For patent licensing inquiries:</p>
            <a href="mailto:patents@utrtherapeutics.com" className="text-blue-600 hover:underline">
              patents@utrtherapeutics.com
            </a>
          </div>
          */}
        </div>
      )
    },
    
    {
      id: 5,
      title: "How Funding and Mentorship Forged a New Path to Fight Cancer",
      excerpt: "Nigerian-born Chidiebere Awah, MD, PhD, CEO of UTR Therapeutics Inc., has created a technology with potential to not only take on aggressive cancers, but also Alzheimer’s disease, metabolic diseases and more.",
      date: "Sep 7, 2024",
      readTime: "3 min",
      category: "Company",
      imageUrl: "/thumbnail_Awah_CU.webp",
      link: "#",
      content: (
        <div className="space-y-4">
          <p>
            Nigerian-born Chidiebere Awah, MD, PhD, CEO of UTR Therapeutics Inc., has created a technology with potential to not only take on aggressive cancers, but also Alzheimer's disease, metabolic diseases and more.
          </p>
          <p>
            Read the full story at{' '}
            <a 
              href="https://wewillcure.com/insights/oncology/insights_article_how-funding-and-mentorship-forged-a-new-path-to-fight" 
              className="text-blue-600 hover:underline whitespace-nowrap"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://wewillcure.com/insights/oncology/insights_article_how-funding-and-mentorship-forged-a-new-path-to-fight
            </a>
          </p>
        </div>
      )
    },
    {
      id: 6,
      title: "Building a Next-gen mRNA Company to Combat Cancer",
      excerpt: "Interview with our CEO about mRNA technology",
      date: "July 14, 2024",
      readTime: "5 min",
      category: "Company",
      imageUrl: "/tuesday-talk.jpg",
      link: "#",
      content: (
        <div className="space-y-4">
          <p>
            Cure will re-broadcast this interview, the Tuesday talks on Tuesday 16th July, 2024 at 12:30 PM ET.
          </p>
          <p>
            It will air across{' '}
            <a 
              href="https://twitter.com/cure345" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline whitespace-nowrap"
            >
              Twitter @cure345
            </a>
            , on{' '}
            <a 
              href="https://linkedin.com/company/cure" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline whitespace-nowrap"
            >
              LinkedIn
            </a>{' '}
            and on{' '}
            <a 
              href="https://www.wewillcure.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline whitespace-nowrap"
            >
              www.wewillcure.com
            </a>.
          </p>
          <p>
            Cure. UTR Therapeutics CEO Dr. Chidiebere Awah joins Cure CEO Seema Kumar to discuss:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Novel mRNA drugs</li>
            <li>Breakthrough medicine for deadly diseases</li>
          </ul>
        </div>
      )
    },
    {
      id: 7,
      title: "Overriding the Cellular Code with mRNA to Fight Cancer",
      excerpt: "Chidiebere Awah, MD, PhD, was a student in his native Nigeria when he realized that chemotherapies weren’t curing patients with advanced cancers and they needed different therapies.",
      date: "July 6, 2024",
      readTime: "8 min",
      category: "Research",
      imageUrl: "/Isolated_RNA.webp",
      link: "#",
      content: (
        <div className="space-y-6">
          <p>
            Chidiebere Awah, MD, PhD, was a student in his native Nigeria when he realized that chemotherapies weren't curing patients with advanced cancers and they needed different therapies. Throughout his academic travels — which brought him to Madrid, Cambridge in the UK, Hanover, Germany and ultimately New York — he came upon two striking realizations that laid the foundation for what has become his company's distinct therapeutic approach.
          </p>
          
          <p>
            One was that low- and mid-level income countries like Nigeria could benefit from drugs that did not require refrigeration. The other was that mRNA and ribosomes control how tumors respond to anticancer therapies. Today, as CEO of UTR Therapeutics Inc., he has combined both insights to create a shelf-stable drug that targets, overrides and degrades the cellular signals driving cancer growth by interfering with mRNA messaging. Moreover, this technology can be used to design therapeutics for other diseases, such as Alzheimer's. He discussed his journey and the science underlying his discoveries with{' '}
            <a 
              href="https://www.wewillcure.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline whitespace-nowrap"
            >
              Cure
            </a>.
          </p>
    
          <h3 className="text-xl font-bold text-gray-900 mt-6">Defusing a 'Ticking' Oncogene</h3>
          <p>
            Key to the UTR approach is the destabilization of cancer-fueling oncogenes. The company takes its name from the 3′ UTR sequence at the end of genes. "Three prime has a hub for integrating all of the signals in a particular gene to make a complete transcript and make a protein," Awah explained.
          </p>
          
          <p>
            Most oncogenes have an mRNA stabilizing element. UTR's technology engineers the mRNA on this part of the oncogene into an unstable form. When this happens, the body sees it as an error and removes it. It's not unlike defusing a ticking time bomb by cutting and deactivating the wires.
          </p>
          
          <p>
            "The message that comes from oncogenic mRNA is to make tons of proteins, causing the cancer metastasis that kills the patient. When we destabilize it, we override that message," Awah noted. "The body sees this as a problematic message and decides the gene will be degraded and no longer used. Once this oncogenic message is no longer available, cells can go about their business."
          </p>
    
          <h3 className="text-xl font-bold text-gray-900 mt-6">Applications Beyond Cancer</h3>
          <p>
            UTR's preclinical models have validated the technology for 17 human cancers driven by the c-MYC oncogene, including triple-negative breast cancer, and the company has filed an IND with the FDA to proceed to clinical trials. The company also validated the technology for HER2-positive cancers such as breast, esophageal and non-small cell lung cancer, as well as some pediatric cancers.
          </p>
          
          <p>
            Because UTR's technology targets a basic process that can drive the development of diseases other than cancer, the company also has pursued evaluations in preclinical studies of neurogenerative diseases such as Alzheimer's and frontotemporal dementia, rare diseases such as gangliosidosis and metabolic diseases, including liver disease.
          </p>
    
          <h3 className="text-xl font-bold text-gray-900 mt-6">Critical Support Systems</h3>
          <p>
            Critical to the company's success to date has been the support Awah and his team have received, such as business coaching through{' '}
            <a 
              href="https://www.nucleate.xyz" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline whitespace-nowrap"
            >
              Nucleate
            </a>{' '}
            (which supports emerging biotech companies) and financing, including seed funds received through{' '}
            <a 
              href="https://www.deerfield.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline whitespace-nowrap"
            >
              Deerfield Management's XSeed Award program
            </a>{' '}
            in 2023. The New York City Economic Development Corporation provided interns, who were fully paid for and whom UTR trained.
          </p>
          
          <p>
            "The first thing you have to have is solid science. That's what will make you able to go to bed at night," he contended. "And then somewhere along the line, the sun is going to shine. You have to hang in."
          </p>
    
          <h3 className="text-xl font-bold text-gray-900 mt-6">Hope for Transport Abroad</h3>
          <p>
            In addition to hoping for promising results from future clinical trials, Awah is encouraged that the drug could be easily transported wherever it is needed.
          </p>
          
          <p>
            "Our drug is quite stable at room temperature. We hope to take it back to Nigeria, to India, to all low-resource and low-infrastructure settings," he concluded. "You could take it on a plane, get on a train and even take it where there is no constant electricity. This will be the breakthrough we've been looking for where other therapies fail or are not available."
          </p>
    
          <div className="bg-blue-50 p-4 rounded-lg mt-6">
            <p className="text-sm text-gray-700">
              *The 2024 XSeed awards will provide two teams up to $250,000 for New York City minority- and women-led life science and healthcare startups working on novel drug development projects. Additionally, the winning teams will join the ecosystem of{' '}
              <a 
                href="https://www.wewillcure.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline whitespace-nowrap"
              >
                Cure
              </a>, a healthcare innovation campus in New York City. The teams will receive peer-learning and office hours with investors, entrepreneurs, and business experts.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 8,
      title: "How to Build a Next-gen MRNA Company to Combat Cancer",
      excerpt: "Event by Cure. UTR Therapeutics CEO and genome engineer Dr. Chidiebere Awah joins Cure CEO Seema Kumar to explain the science and how he developed his company to deploy first–in–class mRNA overwriting therapies and what this means for a wide range of diseases and disorders.",
      date: "Feb 29, 2024",
      readTime: "10 min",
      category: "Research",
      imageUrl: "/cure.png",
      link: "https://www.youtube.com/watch?v=Uvo8KV_aryk",
      isYoutube: true,
      content: 'Event by Cure.UTR Therapeutics CEO and genome engineer Dr. Chidiebere Awah joins Cure CEO Seema Kumar to explain the science and how he developed his company to deploy first–in–class mRNA overwriting therapies and what this means for a wide range of diseases and disorders.'
    },
    {
      id: 9,
      title: "How mRNA Therapies & Genome Engineering Are Reframing How We Fight Cancer",
      excerpt: "This week on Tuesday Talks, learn the latest on how genome engineers are using mRNA therapies to build powerful new tools to treat cancer",
      date: "Feb 26, 2024",
      readTime: "10 min",
      category: "Company",
      imageUrl: "/curetuesday.jpg",
      link: "",
      content: (
        <div className="space-y-4">
          <p>
            This week on <strong>Tuesday Talks</strong>, learn the latest on how genome engineers are using mRNA therapies to build powerful new tools to treat cancer, neurodegenerative disorders, metabolic diseases, and more.
          </p>
          <p>
            UTR Therapeutics CEO and genome engineer Dr. Chidiebere Awah will join Cure CEO Seema Kumar to explain the science and how his company is developing and deploying first–in-class mRNA overwriting therapies—and what this means for a wide range of diseases and disorders.
          </p>
          <p>
            Join the conversation on LinkedIn:{' '}
            <a
              href="https://www.linkedin.com/events/howmrnatherapies-genomeengineer7167908054338891777/"
              className="text-blue-600 hover:underline whitespace-nowrap"
              target="_blank"
              rel="noopener noreferrer"
            >
              Tuesday Talks Event Link
            </a>
          </p>
        </div>
      )
      
    },
    {
      id: 10,
      title: "Deerfield Management Announces 2023 XSeed Award Winners",
      excerpt: "Two Minority-Led Life Science Startup Companies Awarded $250,000 Grants and Access to Cure® Ecosystem",
      date: "April 4, 2023",
      readTime: "10 min",
      category: "Events",
      imageUrl: "/XSeed_Award.jpg",
      link: "",
      content: (
        <div className="space-y-4">
          <p>
            <strong>NEW YORK, April 3, 2023</strong> — Deerfield Management Company and Advancium Health Network announced the winners of the third annual <strong>XSeed Award</strong>, providing each recipient a <strong>$250,000 grant</strong> to support business growth. The XSeed Award supports women- and minority-led life science startups in New York City, helping bridge the gap from translational research to commercial success.
          </p>
          <p>
            The award ceremony was held at the Cure® healthcare innovation campus and featured remarks from the NYC Economic Development Corporation. The event was live-streamed on Deerfield Management’s LinkedIn.
          </p>
          <p>
            <strong>2023 XSeed Award Winners:</strong>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>Rohini Kalvakuntla</strong>, Co-Founder & Chief Growth Officer, <em>Helex</em> – Developing novel 3D genome structures and epigenetics-driven in vivo gene editing therapeutics for retinal dystrophies.
              </li>
              <li>
                <strong>Dr. Chidiebere Awah</strong>, Principal Investigator, <em>UTR Therapeutics, Inc.</em> – Developing a platform to overwrite oncogenic transcripts like c-MYC and HER2, targeting cancers such as metastatic TNBC and NSCLC.
              </li>
            </ul>
          </p>
          <p>
            The winners were selected by a panel of industry leaders including Claire Pomeroy (Lasker Foundation), Nancy Thornberry (Kallyope), Elise Wang (Deerfield), and others. In addition to funding, awardees gain access to Cure’s innovation campus, networking, and mentorship opportunities as part of a supportive cohort.
          </p>
          <p>
            <strong>Quotes:</strong><br />
            “The XSeed Award ensures the life sciences industry reflects the diversity of our city,” said NYC Council Member Carlina Rivera. Manhattan Borough President Mark Levine added, “This award is a critical platform for elevating life science startups led by underrepresented identities.”
          </p>
          <p>
            <a
              href="https://www.xseedaward.com"
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about the XSeed Award →
            </a>
          </p>
          <p>
            <strong>Sources:</strong><br />
            <a
              href="https://www.prnewswire.com/news-releases/deerfield-management-announces-2023-xseed-award-winners-301788501.html"
              className="text-blue-600 hover:underline block"
              target="_blank"
              rel="noopener noreferrer"
            >
              PR Newswire Article
            </a>
            <a
              href="https://markets.businessinsider.com/news/stocks/deerfield-management-announces-2023-xseed-award-winners-1032211321"
              className="text-blue-600 hover:underline block"
              target="_blank"
              rel="noopener noreferrer"
            >
              Business Insider
            </a>
            <a
              href="https://www.wsaz.com/prnewswire/2023/04/03/deerfield-management-announces-2023-xseed-award-winners/?outputType=amp"
              className="text-blue-600 hover:underline block"
              target="_blank"
              rel="noopener noreferrer"
            >
              WSAZ News
            </a>
          </p>
        </div>
      )
      
    },
    {
      id: 11,
      title: "The pre IND product UTRxM1-18 targeting c-MYC",
      excerpt: "The pre IND product UTRxM1-18 targeting c-MYC in TNBC and across other cancers received thorough feedback and guidance from the FDA.",
      date: "Aug 11, 2023",
      readTime: "10 min",
      category: "Research",
      imageUrl: "curetuesday.jpg",
      link: "#",
      content: (
        <div className="space-y-4">
          <p>
            <strong>July 31, 2023</strong> — UTR Therapeutics’ pre-IND product <strong>UTRxM1-18</strong>, which targets <strong>c-MYC</strong> in triple-negative breast cancer (TNBC) and other cancers, received comprehensive feedback and guidance from the <strong>FDA</strong>.
          </p>
          <p>
            We extend our gratitude to all our dedicated <strong>team members</strong>, <strong>mentors</strong>, <strong>funders</strong>, and <strong>strategic partners</strong>, including <em>Thermo Fisher Scientific Inc.</em>, for their invaluable support as we advance this innovative therapy toward clinical development.
          </p>
        </div>
      )
      
    },
    {
      id: 12,
      title: "40 Under 40 in Cancer Awards Event",
      excerpt: "Recognizing emerging leaders in cancer research and treatment",
      date: "May 25, 2023",
      readTime: "10 min",
      category: "Events",
      imageUrl: "/40under40-Party-Banner_1200x630.webp",
      link: "#",
      content: (
        <div className="space-y-4">
          <p>
            40 Under 40 in Cancer is an awards initiative to identify and recognize the contributions being made across the field of cancer by rising stars and emerging leaders under the age of 40 years. Finalists and awardees will be selected by a panel of reviewers from across diverse roles in oncology.
          </p>
          <p>
            <a 
              href="https://www.40under40incancer.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline whitespace-nowrap inline-flex items-center"
            >
              Learn more
              <ArrowRight className="ml-1 h-4 w-4" />
            </a>
          </p>
        </div>
      )
    },
    {
      id: 13,
      title: "Chidiebere Awah MD PhD receives 40 under 40 in Cancer",
      excerpt: "Chidiebere Awah MD PhD receives 40 under 40 in Cancer",
      date: "May 10, 2023",
      readTime: "10 min",
      category: "Events",
      imageUrl: "/chidi.jpg",
      link: "#",
      content: (
        <div className="space-y-4">
          <p>
            The <strong>40 Under 40 in Cancer</strong> awards initiative—supported by leading organizations such as the <strong>Association for Value-Based Cancer Care (AVBCC)</strong>, <strong>NCODA</strong>, <strong>Swim Across America</strong>, <strong>Amplity Health</strong>, <strong>Servier</strong>, <strong>Takeda</strong>, <strong>Jasper Health</strong>, <strong>BeiGene</strong>, and <strong>Cumberland Pharmaceuticals</strong>—recognizes the rising stars and emerging leaders making a difference in the field of oncology.
          </p>
          <p>
            This prestigious recognition celebrates the accomplishments of individuals under 40 who are making meaningful contributions to improve the lives of those affected by cancer. It is an honor to be named among the nation’s most promising young oncology professionals.
          </p>
        </div>
      )
      
    },
    // 
    // Add more news items...
  ];

  const categories = ['All', 'Research', 'Company', 'Events', 'Partnerships'];

  const filteredNews = newsData.filter(item => {
    const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         item.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleNewsClick = (item: NewsItem) => {
    if (item.isYoutube) {
      setSelectedNews(item);
      setShowModal(true);
    } else if (!item.link || item.link === "#") {
      setSelectedNews(item);
      setShowModal(true);
    } else {
      window.open(item.link, '_blank');
    }
  };

  const extractYoutubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>News & Updates | UTR Therapeutics</title>
        <meta name="description" content="Latest news and updates from UTR Therapeutics" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-900 to-purple-900 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            News & Updates
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-blue-100 max-w-3xl mx-auto"
          >
            Stay updated with the latest developments at UTR Therapeutics
          </motion.p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Filters and Search */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <motion.button
                  key={category}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  {category}
                </motion.button>
              ))}
            </div>

            {/* Search */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full md:w-auto"
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search news..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-4 pr-10 py-2 w-full md:w-64 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute right-3 top-2.5 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* News Grid */}
        {filteredNews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="h-48 bg-gray-200 overflow-hidden">
                  <img 
                    src={item.imageUrl} 
                    alt={item.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`}>
                      {item.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{item.date}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{item.readTime} read</span>
                    </div>
                  </div>
                  <motion.button
                    onClick={() => handleNewsClick(item)}
                    whileHover={{ x: 5 }}
                    className="mt-4 inline-flex items-center text-blue-600 font-medium group"
                  >
                    Read more
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-xl font-medium text-gray-700">No news articles found</h3>
            <p className="text-gray-500 mt-2">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* News Modal */}
      {showModal && selectedNews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-gray-900">{selectedNews.title}</h3>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {selectedNews.isYoutube ? (
                <div className="aspect-w-16 aspect-h-9 mb-6">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYoutubeId(selectedNews.link)}`}
                    title={selectedNews.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-96 rounded-lg"
                  ></iframe>
                </div>
              ) : (
                <div className="h-64 bg-gray-200 rounded-lg mb-6 overflow-hidden">
                  <img 
                    src={selectedNews.imageUrl} 
                    alt={selectedNews.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="flex items-center text-sm text-gray-500 mb-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedNews.category)}`}>
                  {selectedNews.category}
                </span>
                <span className="mx-2">•</span>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>{selectedNews.date}</span>
                </div>
              </div>

              <div className="prose max-w-none">
                {selectedNews.isYoutube ? (
                  <p className="text-gray-700 mb-4">
                    Watch the full interview with our CEO discussing {selectedNews.title.toLowerCase()}.
                  </p>
                ) : (
                  <>
                    <p className="text-gray-700 mb-4">{selectedNews.excerpt}</p>
                    <p className="text-gray-700">{selectedNews.content}</p>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

<div className="w-full">
        <Contact />
      </div>
    </div>
  );
};

// Helper function for category colors
const getCategoryColor = (category: string): string => {
  switch(category) {
    case 'Research':
      return 'bg-blue-100 text-blue-800';
    case 'Company':
      return 'bg-purple-100 text-purple-800';
    case 'Events':
      return 'bg-green-100 text-green-800';
    case 'Partnerships':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default News;